import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import { buildPriorTurns, consumeSseStream, genId, MAX_QUESTION_CHARS, normalizeStreamError } from '../../utils/chatbot-sse';
import { dispatch } from '../index';

// ==============================|| CHATBOTS SLICE ||============================== //
//
// The chat endpoint streams its response over SSE. The API contract is: each
// request opens a NEW SSE stream, the server emits zero or more `lookup_started`
// / `lookup_outcome` progress events, then exactly ONE terminal event (`answer`
// or `error`), after which the stream ends. The intermediate progress events are
// intentionally ignored — only the final answer is surfaced. We build a fresh
// connection per message and tear it down on the terminal event. Pure SSE helpers
// live in utils/chatbot-sse so they can be unit-tested in isolation.
//
// We reuse the shared axios instance (responseType 'stream' + the fetch adapter)
// so the Authorization / X-Synergy-Subdomain / X-Synergy-Client-Type-Id headers
// are injected automatically and 401 goes through the existing interceptor —
// while still handing us a ReadableStream we can parse SSE from by hand.

// Keep the in-session conversation bounded so a long session does not grow the
// store unboundedly (history is memory-only and not persisted).
const MAX_MESSAGES = 100;

// Holds the in-flight request's AbortController so a new message (or an explicit
// stop) can cancel the previous stream. Kept at module scope because it is not
// serializable and must not live in the Redux store.
let currentAbort = null;

const initialState = {
  open: false,
  streaming: false,
  // messages: ordered conversation for rendering.
  //   user      -> { id, role: 'user', content }
  //   assistant -> { id, role: 'assistant', text, structured, status, error }
  //                 status: 'streaming' | 'done' | 'error'
  messages: []
};

const slice = createSlice({
  name: 'chatbots',
  initialState,
  reducers: {
    // UI: open / close / toggle the drawer
    openChatbot(state) {
      state.open = true;
    },
    closeChatbot(state) {
      state.open = false;
    },
    toggleChatbot(state) {
      state.open = !state.open;
    },

    // Clear the whole conversation
    clearMessages(state) {
      state.messages = [];
    },

    // Append the user's question, then a placeholder assistant message that the
    // stream fills in as events arrive.
    startExchange(state, action) {
      const { userMessage, assistantId } = action.payload;
      state.streaming = true;
      state.messages.push(userMessage);
      state.messages.push({
        id: assistantId,
        role: 'assistant',
        text: '',
        structured: null,
        status: 'streaming',
        error: null
      });
      if (state.messages.length > MAX_MESSAGES) {
        state.messages = state.messages.slice(-MAX_MESSAGES);
      }
    },

    // Terminal `answer` event
    resolveExchange(state, action) {
      const { assistantId, text, structured } = action.payload;
      const msg = state.messages.find((m) => m.id === assistantId);
      if (msg) {
        msg.text = text;
        msg.structured = structured || null;
        msg.status = 'done';
      }
      state.streaming = false;
    },

    // Terminal `error` event, transport error, or abort
    failExchange(state, action) {
      const { assistantId, error } = action.payload;
      const msg = state.messages.find((m) => m.id === assistantId);
      if (msg) {
        msg.status = 'error';
        msg.error = error;
      }
      state.streaming = false;
    }
  }
});

export default slice.reducer;

export const { openChatbot, closeChatbot, toggleChatbot, clearMessages } = slice.actions;

// --- thunks ----------------------------------------------------------------

// Cancel any in-flight stream (new message supersedes the old one, or user
// closes the drawer mid-stream).
export function stopStreaming() {
  return async () => {
    if (currentAbort) {
      currentAbort.abort();
      currentAbort = null;
    }
  };
}

export function sendMessage(question) {
  return async (_dispatch, getState) => {
    const trimmed = (question || '').trim().slice(0, MAX_QUESTION_CHARS);
    if (!trimmed) return;

    // Supersede any previous in-flight stream.
    if (currentAbort) {
      currentAbort.abort();
    }
    const abortController = new AbortController();
    currentAbort = abortController;

    const priorTurns = buildPriorTurns(getState().chatbots.messages);

    const userMessage = { id: genId(), role: 'user', content: trimmed };
    const assistantId = genId();
    dispatch(slice.actions.startExchange({ userMessage, assistantId }));

    let reader = null;

    try {
      // Reuse the shared axios instance: it injects the auth/client headers and
      // its interceptor handles 401 (removes token + redirects to /login). The
      // fetch adapter + responseType 'stream' still hands us a ReadableStream so
      // we can consume the SSE events incrementally. axios rejects on non-2xx,
      // so pre-stream HTTP failures land in the catch block below.
      const response = await axios.post(
        '/chatbots/chat',
        { question: trimmed, priorTurns },
        {
          adapter: 'fetch',
          responseType: 'stream',
          headers: { 'Content-Type': 'application/json' },
          signal: abortController.signal
        }
      );

      reader = response.data.getReader();

      // Dispatch per event; return true on a terminal event to stop reading.
      const onEvent = (eventName, payload) => {
        // Intermediate progress events are ignored — only the final answer is
        // surfaced. Keep reading until the terminal event arrives.
        if (eventName === 'lookup_started' || eventName === 'lookup_outcome') {
          return false;
        }
        if (eventName === 'answer') {
          dispatch(slice.actions.resolveExchange({ assistantId, text: payload.text, structured: payload.structured }));
          return true;
        }
        if (eventName === 'error') {
          dispatch(slice.actions.failExchange({ assistantId, error: payload }));
          return true;
        }
        return false;
      };

      const reason = await consumeSseStream(reader, onEvent);

      if (reason === 'overflow') {
        abortController.abort();
        dispatch(slice.actions.failExchange({ assistantId, error: { code: 'SERVICE_UNAVAILABLE', errorMessage: 'Response too large.' } }));
      } else if (reason === 'eof') {
        // Stream closed without a terminal event.
        const msg = getState().chatbots.messages.find((m) => m.id === assistantId);
        if (msg && msg.status === 'streaming') {
          dispatch(
            slice.actions.failExchange({
              assistantId,
              error: { code: 'SERVICE_UNAVAILABLE', errorMessage: 'Connection closed before an answer was received.' }
            })
          );
        }
      }
    } catch (error) {
      // Aborts are intentional (superseded / drawer closed). The axios
      // interceptor rewrites errors (to error.response.data or a string), so we
      // detect cancellation via the signal rather than error.name.
      if (abortController.signal.aborted) {
        dispatch(slice.actions.failExchange({ assistantId, error: { code: 'ABORTED', errorMessage: 'Cancelled.' } }));
      } else {
        // 401 is handled by the interceptor (token cleared + redirect); other
        // failures surface the server-provided code/message when present.
        dispatch(slice.actions.failExchange({ assistantId, error: normalizeStreamError(error) }));
      }
    } finally {
      // Release the stream reader so the underlying connection can be freed
      // (covers abort / parse-error / early-break paths, not just clean EOF).
      if (reader) {
        try {
          await reader.cancel();
        } catch (e) {
          // reader may already be closed/errored — nothing to do
        }
      }
      if (currentAbort === abortController) {
        currentAbort = null;
      }
    }
  };
}
